/**
 * Immutable-original remeasure records for Engineering.
 *
 * A remeasure is not a sketch version and never writes back into geometry. It
 * freezes the complete designer document, extracts every production-relevant
 * measurement into a separate Actual/Remeasure dataset, and records edits only
 * against that dataset. Engineering therefore receives a complete set even
 * when a field value was unchanged, while the designer original remains intact.
 */
import { parseLength } from '../geometry/length.js';

const clone = (value) => JSON.parse(JSON.stringify(value));
const nowOf = (value) => value ?? new Date().toISOString();
const finite = (value) => (Number.isFinite(value) ? value : null);

function wallUnits(wall, scale) {
  if (wall?.isMeasured && Number.isFinite(wall.lengthUnits)) return wall.lengthUnits;
  if (!wall?.start || !wall?.end) return null;
  const px = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  return px / (scale > 0 ? scale : 1);
}

function measurement(key, group, label, value, sourceId = null) {
  const originalUnits = finite(value);
  if (originalUnits === null) return null;
  return { key, group, label, sourceId, unit: 'in', originalUnits, actualUnits: originalUnits };
}

/** Extracts the production measurement schedule from a serialized sketch. */
export function productionMeasurements(sketch) {
  if (!sketch || !Array.isArray(sketch.walls)) throw new Error('A serialized designer sketch is required.');
  const rows = [];
  const add = (...args) => { const row = measurement(...args); if (row) rows.push(row); };
  const scale = finite(sketch.pxPerUnit) ?? finite(sketch.scalePxPerUnit) ?? 1;
  const wallLabels = new Map(sketch.walls.map((wall) => [wall.id, `W${wall.wallNumber ?? '?'}`]));

  for (const wall of sketch.walls) {
    if ((wall.segmentKind ?? 'wall') !== 'wall') continue;
    const label = wallLabels.get(wall.id);
    add(`wall:${wall.id}:length`, 'Walls', `${label} length`, wallUnits(wall, scale), wall.id);
  }
  add('room:ceilingHeight', 'Room', 'Ceiling height', sketch.ceilingHeightUnits, sketch.id);
  add('room:doorHeight', 'Room', 'Default door height', sketch.doorHeightUnits, sketch.id);

  for (const [wallId, value] of Object.entries(sketch.cornerHeights ?? {})) {
    add(`corner:${wallId}:height`, 'Ceiling', `${wallLabels.get(wallId) ?? 'Wall'} end height`, value, wallId);
  }
  for (const obstruction of sketch.obstructions ?? []) {
    const host = wallLabels.get(obstruction.wallId) ?? 'Wall';
    const name = `${host} · ${obstruction.type}`;
    add(`obstruction:${obstruction.id}:offsetAlongWall`, 'Openings & obstructions', `${name} from wall start`, obstruction.offsetAlongWall, obstruction.id);
    add(`obstruction:${obstruction.id}:offsetFromFloor`, 'Openings & obstructions', `${name} from floor`, obstruction.offsetFromFloor, obstruction.id);
    add(`obstruction:${obstruction.id}:width`, 'Openings & obstructions', `${name} width`, obstruction.widthUnits, obstruction.id);
    if (obstruction.type === 'wall_opening') {
      add(`obstruction:${obstruction.id}:clearOpeningWidth`, 'Openings & obstructions',
        `${name} clear opening inside casing`, obstruction.opening?.clearOpeningWidthUnits, obstruction.id);
      add(`obstruction:${obstruction.id}:swingProjection`, 'Openings & obstructions',
        `${name} swing projection`, obstruction.opening?.swingProjectionUnits, obstruction.id);
    }
    add(`obstruction:${obstruction.id}:height`, 'Openings & obstructions', `${name} height`, obstruction.heightUnits, obstruction.id);
    add(`obstruction:${obstruction.id}:depth`, 'Openings & obstructions', `${name} depth`, obstruction.depthUnits, obstruction.id);
    // The sketch stores recesses as a negative face offset. A field technician
    // should remeasure the positive physical inset, not type a negative number.
    if (Number.isFinite(obstruction.faceOffsetUnits)) {
      add(`obstruction:${obstruction.id}:inset`, 'Openings & obstructions', `${name} inset`, Math.max(0, -obstruction.faceOffsetUnits), obstruction.id);
    }
  }
  for (const feature of sketch.ceilingOpenings ?? []) {
    const name = ({ attic_hatch: 'Attic hatch', attic_stairs: 'Attic stairs', ceiling_fan: 'Ceiling fan', ceiling_light: 'Ceiling light', ceiling_sprinkler: 'Sprinkler' })[feature.kind] ?? 'Room obstruction';
    add(`ceiling:${feature.id}:fromReference1`, 'Room obstructions', `${name} from reference 1`, feature.xUnits, feature.id);
    add(`ceiling:${feature.id}:fromReference2`, 'Room obstructions', `${name} from reference 2`, feature.yUnits, feature.id);
    if (feature.kind === 'ceiling_fan') {
      add(`ceiling:${feature.id}:diameter`, 'Room obstructions', `${name} diameter`, feature.diameterUnits ?? feature.widthUnits, feature.id);
      add(`ceiling:${feature.id}:fromFloor`, 'Room obstructions', `${name} distance from finished floor (FF)`, feature.fromFloorUnits, feature.id);
    } else {
      add(`ceiling:${feature.id}:width`, 'Room obstructions', `${name} width`, feature.widthUnits, feature.id);
      add(`ceiling:${feature.id}:length`, 'Room obstructions', `${name} length`, feature.lengthUnits, feature.id);
      add(`ceiling:${feature.id}:drop`, 'Room obstructions', `${name} drop below ceiling`, feature.heightUnits, feature.id);
    }
    add(`ceiling:${feature.id}:stairFloorBase`, 'Room obstructions', `${name} floor base from reference 2`, feature.ladderFloorOffsetUnits, feature.id);
  }
  return rows;
}

/** Starts a production remeasure while freezing the designer original verbatim. */
export function createRemeasureSession({ id, sketch, technician = '', nowIso } = {}) {
  if (!id) throw new Error('Remeasure session id is required.');
  const originalSketch = clone(sketch);
  const createdAt = nowOf(nowIso);
  return {
    schemaVersion: 1, id, sourceSketchId: originalSketch.id,
    status: 'remeasuring', technician: String(technician ?? '').trim(),
    originalCapturedAt: createdAt, originalSketch,
    measurements: productionMeasurements(originalSketch),
    createdAt, updatedAt: createdAt, completedAt: null,
  };
}

/** Updates only Actual/Remeasure; the original snapshot and values are untouched. */
export function setRemeasureValue(session, key, rawValue, { nowIso } = {}) {
  const next = deserializeRemeasureSession(session);
  if (next.status === 'ready_for_engineering') throw new Error('A completed remeasure is locked for Engineering.');
  const row = next.measurements.find((item) => item.key === key);
  if (!row) throw new Error('The remeasure field no longer exists.');
  const parsed = typeof rawValue === 'number' ? rawValue : parseLength(String(rawValue ?? ''));
  if (!Number.isFinite(parsed) || parsed < 0) throw new Error('Enter a non-negative measurement.');
  row.actualUnits = parsed;
  next.updatedAt = nowOf(nowIso);
  return next;
}

/** Locks the separate production dataset; it still never changes the sketch. */
export function completeRemeasureSession(session, { technician, nowIso } = {}) {
  const next = deserializeRemeasureSession(session);
  const at = nowOf(nowIso);
  if (technician !== undefined) next.technician = String(technician ?? '').trim();
  if (!next.technician) throw new Error('Enter the remeasure technician before sending to Engineering.');
  next.status = 'ready_for_engineering';
  next.completedAt = at;
  next.updatedAt = at;
  return next;
}

/** Actual/original/variance rows consumed by the UI and printable report. */
export function remeasureReportRows(session) {
  const valid = deserializeRemeasureSession(session);
  return valid.measurements.map((row) => ({
    ...clone(row), varianceUnits: row.actualUnits - row.originalUnits,
    changed: Math.abs(row.actualUnits - row.originalUnits) > 1e-9,
  }));
}

export function serializeRemeasureSession(session) { return deserializeRemeasureSession(session); }

export function deserializeRemeasureSession(payload) {
  const value = clone(payload);
  if (!value || value.schemaVersion !== 1 || !value.id || !value.originalSketch || !Array.isArray(value.measurements)) {
    throw new Error('Invalid remeasure session payload.');
  }
  for (const row of value.measurements) {
    if (!row?.key || !Number.isFinite(row.originalUnits) || !Number.isFinite(row.actualUnits)) {
      throw new Error('Invalid remeasure measurement payload.');
    }
  }
  return value;
}
